import { RevenueMarketSelect, parseRevenueMarket, type RevenueMarket } from '@/components/travel/revenue-market';
import { DirectionSelect, parseDirection, type Direction } from '@/components/travel/direction';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface BookingFormData {
  customerId: string;
  packageId?: string;
  travelStartDate: string;
  travelEndDate: string;
  totalAdults: number;
  totalChildren: number;
  totalInfants: number;
  totalAmount: number;
  currency: string;
  specialRequests?: string;
  internalNotes?: string;
}

interface BookingModalProps {
  defaultDirection?: Direction;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (bookingId: string) => void;
}

function getTodayString(): string {
  return new Date().toISOString().split('T')[0] ?? '';
}

function getNextWeekString(): string {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return date.toISOString().split('T')[0] ?? '';
}

export function BookingModal({
  open,
  onOpenChange,
  onSuccess,
  defaultDirection,
}: BookingModalProps) {
  const { t } = useTranslation('customers');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [revenueMarket, setRevenueMarket] = useState<RevenueMarket | undefined>();
  const [direction, setDirection] = useState<Direction | undefined>(defaultDirection);
  useEffect(() => { if (open) { setDirection(defaultDirection); setRevenueMarket(undefined); } }, [open, defaultDirection]);
  const [paymentOnSpot, setPaymentOnSpot] = useState(false);
  const user = useAuthStore((state) => state.user);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<BookingFormData>({
    defaultValues: {
      totalAdults: 1,
      totalChildren: 0,
      totalInfants: 0,
      totalAmount: 0,
      currency: 'USD',
      travelStartDate: getTodayString(),
      travelEndDate: getNextWeekString(),
    },
  });

  const { data: customers, isLoading: customersLoading } = trpc.customer.list.useQuery({
    page: 1,
  });

  const { data: packages, isLoading: packagesLoading } = trpc.package.list.useQuery({
    page: 1,
  });

  const watchedCustomerId = watch('customerId');
  const watchedTotalAmount = watch('totalAmount') || 0;

  // Credit check for the selected customer
  const { data: creditCheck, isLoading: creditCheckLoading } = trpc.customer.canBook.useQuery(
    {
      customerId: watchedCustomerId,
      amount: watchedTotalAmount,
    },
    {
      enabled: !!watchedCustomerId && watchedTotalAmount > 0,
    }
  );

  // Reset payment on spot when customer or amount changes
  useEffect(() => {
    setPaymentOnSpot(false);
  }, [watchedCustomerId, watchedTotalAmount]);

  const utils = trpc.useUtils();

  const createMutation = trpc.booking.create.useMutation({
    onSuccess: (data) => {
      utils.booking.list.invalidate();
      utils.booking.stats.invalidate();
      reset();
      onOpenChange(false);
      onSuccess?.(data.id);
      showSuccessToast('Booking created successfully');
    },
    onError: (error) => {
      setIsSubmitting(false);
      showErrorToast(error.message || 'Failed to create booking');
    },
  });

  const watchedPackageId = watch('packageId');
  const watchedCurrency = watch('currency') || 'USD';
  const watchedAdults = watch('totalAdults') || 0;
  const watchedChildren = watch('totalChildren') || 0;
  const watchedInfants = watch('totalInfants') || 0;

  // Auto-fill price when package is selected
  const selectedPackage = packages?.packages.find((p) => p.id === watchedPackageId);

  useEffect(() => {
    if (selectedPackage && selectedPackage.pricing.length > 0) {
      const pricing = selectedPackage.pricing[0];
      if (pricing) {
        // Get price per person based on group size
        const totalPax = watchedAdults + watchedChildren;
        const getDecimalValue = (val: unknown): number => {
          if (typeof val === 'object' && val !== null && 'toNumber' in val) {
            return (val as { toNumber: () => number }).toNumber();
          }
          return Number(val) || 0;
        };

        let pricePerPerson = getDecimalValue(pricing.price2Pax);
        if (totalPax >= 8 && pricing.price8PlusPax) {
          pricePerPerson = getDecimalValue(pricing.price8PlusPax);
        } else if (totalPax >= 6 && pricing.price6Pax) {
          pricePerPerson = getDecimalValue(pricing.price6Pax);
        } else if (totalPax >= 4 && pricing.price4Pax) {
          pricePerPerson = getDecimalValue(pricing.price4Pax);
        }

        // Calculate child discount (percentage)
        const childDiscountPercent = pricing.childDiscount
          ? getDecimalValue(pricing.childDiscount)
          : 0;
        const childPrice = pricePerPerson * (1 - childDiscountPercent / 100);

        // Infants are typically free or minimal cost
        const infantPrice = 0;

        const total = (watchedAdults * pricePerPerson) +
                      (watchedChildren * childPrice) +
                      (watchedInfants * infantPrice);
        setValue('totalAmount', Math.round(total * 100) / 100);
        setValue('currency', pricing.currency);
      }
    }
  }, [selectedPackage, watchedAdults, watchedChildren, watchedInfants, setValue]);

  const totalTravelers = watchedAdults + watchedChildren + watchedInfants;

  const onSubmit = (data: BookingFormData) => {
    if (!user?.id) return;

    setIsSubmitting(true);
    createMutation.mutate({
      customerId: data.customerId,
      direction,
      revenueMarket,
      packageId: data.packageId === '_none' ? undefined : data.packageId,
      agentId: user.id,
      travelStartDate: new Date(data.travelStartDate),
      travelEndDate: new Date(data.travelEndDate),
      totalAdults: data.totalAdults,
      totalChildren: data.totalChildren,
      totalInfants: data.totalInfants,
      totalAmount: data.totalAmount,
      currency: data.currency,
      specialRequests: data.specialRequests,
      internalNotes: data.internalNotes,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Booking</DialogTitle>
          <DialogDescription>
            Create a new booking for a customer.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <DirectionSelect value={direction} onChange={value => setDirection(parseDirection(value))} />
          <RevenueMarketSelect value={revenueMarket} onChange={value => setRevenueMarket(parseRevenueMarket(value))} />
          {/* Customer and Package */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Customer *</Label>
              <Combobox
                options={
                  customersLoading
                    ? []
                    : (customers?.customers.map((customer) => ({
                        value: customer.id,
                        label: `${customer.firstName} ${customer.lastName}`,
                      })) ?? [])
                }
                value={watchedCustomerId || ''}
                onValueChange={(value) => setValue('customerId', value)}
                placeholder="Select customer"
                searchPlaceholder="Search customers..."
              />
              {errors.customerId && (
                <p className="text-sm text-red-500">{errors.customerId.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Package (Optional)</Label>
              <Combobox
                options={[
                  { value: '_none', label: 'No Package (Custom)' },
                  ...(packagesLoading
                    ? []
                    : (packages?.packages.map((pkg) => ({
                        value: pkg.id,
                        label: `${pkg.name} (${pkg.code})`,
                      })) ?? [])),
                ]}
                value={watchedPackageId || '_none'}
                onValueChange={(value) => setValue('packageId', value === '_none' ? undefined : value)}
                placeholder="Select package"
                searchPlaceholder="Search packages..."
              />
              {selectedPackage && (
                <p className="text-xs text-muted-foreground">
                  {selectedPackage.duration} days • From{' '}
                  {selectedPackage.pricing[0] && formatCurrency(
                    typeof selectedPackage.pricing[0].price2Pax === 'object'
                      ? (selectedPackage.pricing[0].price2Pax as { toNumber: () => number }).toNumber()
                      : Number(selectedPackage.pricing[0].price2Pax),
                    selectedPackage.pricing[0].currency
                  )} pp
                </p>
              )}
            </div>
          </div>

          {/* Travel Dates */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="travelStartDate">Travel Start Date *</Label>
              <Input
                id="travelStartDate"
                type="date"
                {...register('travelStartDate', { required: 'Start date is required' })}
              />
              {errors.travelStartDate && (
                <p className="text-sm text-red-500">{errors.travelStartDate.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="travelEndDate">Travel End Date *</Label>
              <Input
                id="travelEndDate"
                type="date"
                {...register('travelEndDate', { required: 'End date is required' })}
              />
              {errors.travelEndDate && (
                <p className="text-sm text-red-500">{errors.travelEndDate.message}</p>
              )}
            </div>
          </div>

          {/* Travelers */}
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="totalAdults">Adults *</Label>
              <Input
                id="totalAdults"
                type="number"
                min="1"
                {...register('totalAdults', {
                  required: 'At least 1 adult required',
                  valueAsNumber: true,
                  min: { value: 1, message: 'At least 1 adult required' },
                })}
              />
              {errors.totalAdults && (
                <p className="text-sm text-red-500">{errors.totalAdults.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="totalChildren">Children (2-11)</Label>
              <Input
                id="totalChildren"
                type="number"
                min="0"
                {...register('totalChildren', {
                  valueAsNumber: true,
                  min: 0,
                })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="totalInfants">Infants (0-2)</Label>
              <Input
                id="totalInfants"
                type="number"
                min="0"
                {...register('totalInfants', {
                  valueAsNumber: true,
                  min: 0,
                })}
              />
            </div>
          </div>

          {/* Pricing */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="totalAmount">Total Amount *</Label>
              <Input
                id="totalAmount"
                type="number"
                step="0.01"
                min="0"
                {...register('totalAmount', {
                  required: 'Amount is required',
                  valueAsNumber: true,
                  min: { value: 0, message: 'Amount must be positive' },
                })}
              />
              {errors.totalAmount && (
                <p className="text-sm text-red-500">{errors.totalAmount.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Combobox
                options={[
                  { value: 'USD', label: 'USD - US Dollar' },
                  { value: 'EUR', label: 'EUR - Euro' },
                  { value: 'GBP', label: 'GBP - British Pound' },
                  { value: 'TRY', label: 'TRY - Turkish Lira' },
                ]}
                value={watchedCurrency}
                onValueChange={(value) => setValue('currency', value)}
                placeholder="Select currency"
                searchPlaceholder="Search currencies..."
              />
            </div>
          </div>

          {/* Notes */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="specialRequests">Special Requests</Label>
              <Textarea
                id="specialRequests"
                {...register('specialRequests')}
                placeholder="Customer special requests..."
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="internalNotes">Internal Notes</Label>
              <Textarea
                id="internalNotes"
                {...register('internalNotes')}
                placeholder="Internal notes (not visible to customer)..."
                rows={3}
              />
            </div>
          </div>

          {/* Credit Limit Warning */}
          {creditCheck && !creditCheck.canBook && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/50">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
                <div className="space-y-3 flex-1">
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-200">
                      {t('credit.creditExceeded')}
                    </p>
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      {t('credit.creditExceededDescription')}
                    </p>
                  </div>
                  {creditCheck.creditLimit !== undefined && creditCheck.outstandingBalance !== undefined && (
                    <div className="grid gap-1 text-sm text-amber-700 dark:text-amber-300">
                      <div className="flex justify-between">
                        <span>{t('credit.limit')}:</span>
                        <span>{formatCurrency(creditCheck.creditLimit, creditCheck.customer?.id ? 'TRY' : 'USD')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('credit.balance')}:</span>
                        <span>{formatCurrency(creditCheck.outstandingBalance, 'TRY')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('credit.available')}:</span>
                        <span>{formatCurrency(creditCheck.availableCredit ?? 0, 'TRY')}</span>
                      </div>
                      {creditCheck.shortfall !== undefined && (
                        <div className="flex justify-between font-medium">
                          <span>{t('credit.shortfall')}:</span>
                          <span>{formatCurrency(creditCheck.shortfall, 'TRY')}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Payment on spot override */}
                  <label className="flex items-center gap-3 cursor-pointer rounded-md border border-amber-300 bg-white p-3 dark:border-amber-700 dark:bg-amber-900/30">
                    <input
                      type="checkbox"
                      checked={paymentOnSpot}
                      onChange={(e) => setPaymentOnSpot(e.target.checked)}
                      className="h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                    />
                    <div>
                      <p className="font-medium text-amber-800 dark:text-amber-200">
                        {t('credit.paymentOnSpot')}
                      </p>
                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        {t('credit.paymentOnSpotDescription')}
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="rounded-lg bg-muted p-4">
            <h4 className="font-medium mb-2">Booking Summary</h4>
            <div className="grid gap-2 text-sm sm:grid-cols-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Travelers</span>
                <span className="font-medium">{totalTravelers}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Amount</span>
                <span className="font-medium">{formatCurrency(watchedTotalAmount, watchedCurrency)}</span>
              </div>
            </div>
          </div>

          {/* Error Messages */}
          {createMutation.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {createMutation.error.message}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                !watchedCustomerId ||
                creditCheckLoading ||
                (creditCheck && !creditCheck.canBook && !paymentOnSpot)
              }
            >
              {(isSubmitting || creditCheckLoading) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {creditCheck && !creditCheck.canBook && !paymentOnSpot
                ? t('credit.cannotBook')
                : 'Create Booking'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
